import React, { useState } from 'react'
import DatePicker from 'react-datepicker'
import '../../node_modules/react-datepicker/dist/react-datepicker.css'
import '../components/Activities/activity.scss'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import EventIcon from '@mui/icons-material/Event'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { requiredHelperNode } from '../components/Activities/requiredFieldHelperText'

const FieldLabel = ({ label, tooltip, required }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="16x600">
            {label}
            {required && (
                <Typography component="span" sx={{ color: 'error.main' }}>
                    {' '}
                    *
                </Typography>
            )}
        </Typography>
        <Tooltip title={tooltip} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }} />
        </Tooltip>
    </Box>
)

const QuarterInput = React.forwardRef(function QuarterInput(
    { value, onClick, label, disabled, error, helperText },
    ref
) {
    return (
        <TextField
            label={label}
            value={value || ''}
            onClick={onClick}
            inputRef={ref}
            fullWidth
            disabled={disabled}
            error={error}
            helperText={helperText || ' '}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <EventIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                ),
                readOnly: true,
            }}
            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
        />
    )
})

export default function QuarterDatePicker({
    oldStart,
    oldEnd,
    start,
    end,
    disabled,
    startLabel = 'Actual Start Date',
    startTooltip = "The activity's actual initiation date or the first patient's first visit.",
    endLabel = 'Actual End Date',
    endTooltip = "The activity's actual completion date or the last patient's last visit.",
    startRequired,
    endRequired,
    startFieldError,
    endFieldError,
}) {
    const [startDate, setStartDate] = useState(oldStart ? new Date(oldStart) : null)
    const [endDate, setEndDate] = useState(oldEnd ? new Date(oldEnd) : null)

    function handleStartChange(date) {
        setStartDate(date)
        start(date)
    }

    function handleEndChange(date) {
        setEndDate(date)
        end(date)
    }

    return (
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1, '& .react-datepicker-wrapper': { width: '100%' } }}>
                <FieldLabel label={startLabel} tooltip={startTooltip} required={startRequired} />
                <DatePicker
                    customInput={
                        <QuarterInput
                            label="Start Quarter"
                            disabled={disabled}
                            error={!!startFieldError}
                            helperText={requiredHelperNode(startFieldError, startRequired)}
                        />
                    }
                    selected={startDate}
                    onChange={handleStartChange}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="yyyy, QQQ"
                    showQuarterYearPicker
                    disabled={disabled}
                    popperClassName="quarter-picker-popper"
                />
            </Box>
            <Box sx={{ flex: 1, '& .react-datepicker-wrapper': { width: '100%' } }}>
                <FieldLabel label={endLabel} tooltip={endTooltip} required={endRequired} />
                <DatePicker
                    customInput={
                        <QuarterInput
                            label="End Quarter"
                            disabled={disabled}
                            error={!!endFieldError}
                            helperText={requiredHelperNode(endFieldError, endRequired)}
                        />
                    }
                    selected={endDate}
                    onChange={handleEndChange}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="yyyy, QQQ"
                    minDate={startDate}
                    showQuarterYearPicker
                    disabled={disabled}
                    popperClassName="quarter-picker-popper"
                />
            </Box>
        </Box>
    )
}
